/**
 * Lane model and lane ADVICE.
 *
 * Painting arrows was not enough: on a multi-lane approach the driver read the
 * arrows, stayed in the leftmost lane because that is where you sit in Thai
 * traffic, and that lane turned out to be exit-only onto a different road.
 * Arrows tell you what each lane does; they do not tell you *which lane you
 * should be in right now*, and they do not shout when the lane you are
 * probably in is the wrong one.
 *
 * So on top of the arrows we compute:
 *   - the contiguous block of lanes that stays on route, as a position
 *     ("the 2 rightmost of 4"),
 *   - whether a lane change is genuinely required,
 *   - and a TRAP flag: the outermost lane on the side the driver naturally
 *     occupies is not valid. That is the case that nearly went wrong, and it
 *     gets an explicit spoken and visual warning.
 *
 * Mapbox orders `lanes` left-to-right in the direction of travel.
 */

import type { BannerInstruction, Lane, LaneIndication, RouteStep } from '../core/types';

/** Lane data attached to the approach along `step`, or null. */
export function lanesForStep(step: RouteStep | null | undefined): Lane[] | null {
  if (!step?.intersections?.length) return null;
  // The relevant intersection is the last one carrying lanes - that is the
  // junction immediately before the maneuver at the end of this step.
  for (let i = step.intersections.length - 1; i >= 0; i--) {
    const lanes = step.intersections[i].lanes;
    if (lanes?.length) return lanes;
  }
  return null;
}

/**
 * Lanes as Mapbox's own banner renders them. Preferred when present: the sub
 * banner is already filtered to the upcoming maneuver, whereas the raw
 * intersection model can describe a junction further along.
 */
export function lanesFromBanner(banner: BannerInstruction | null): Lane[] | null {
  const components = banner?.sub?.components;
  if (!components?.length) return null;
  const laneComponents = components.filter((c) => c.type === 'lane');
  if (!laneComponents.length) return null;

  return laneComponents.map((c) => ({
    valid: c.active === true,
    active: c.active === true,
    valid_indication: c.active_direction,
    indications: (c.directions ?? []) as LaneIndication[],
  }));
}

/**
 * Best available lane model for the maneuver at the end of `step`.
 *
 * The two sources are merged rather than picked: the banner is authoritative
 * for validity, the intersection model is richer about which arrows each lane
 * is painted with. When they disagree on lane COUNT we trust the banner, since
 * that is what Mapbox's own UI shows for this maneuver.
 */
export function resolveLanes(
  banner: BannerInstruction | null,
  step: RouteStep | null | undefined,
): Lane[] | null {
  const fromBanner = lanesFromBanner(banner);
  const fromRoad = lanesForStep(step);
  if (!fromBanner) return fromRoad;
  if (!fromRoad || fromRoad.length !== fromBanner.length) return fromBanner;

  return fromBanner.map((b, i) => ({
    ...b,
    indications: b.indications.length ? b.indications : fromRoad[i].indications,
    valid_indication: b.valid_indication ?? fromRoad[i].valid_indication,
  }));
}

/** A lane counts as usable when Mapbox marks it valid (or active). */
export function laneValid(lane: Lane): boolean {
  return lane.valid === true || lane.active === true;
}

/** True when at least one lane is unusable - i.e. lane position actually matters. */
export function isLaneConstrained(lanes: Lane[] | null | undefined): boolean {
  if (!lanes?.length) return false;
  const valid = lanes.filter(laneValid).length;
  return valid > 0 && valid < lanes.length;
}

export interface LaneAdvice {
  lanes: Lane[];
  total: number;
  /** Indices (0 = leftmost) of lanes that stay on route. */
  validIndices: number[];
  /** Where the usable block sits across the carriageway. */
  side: 'left' | 'right' | 'centre' | 'all';
  /** How many lanes stay on route. */
  validCount: number;
  /** A lane change may be required - some lanes do not stay on route. */
  changeRequired: boolean;
  /**
   * The outermost lane on the side the driver naturally occupies is NOT valid.
   * In Thailand that is the left lane, and drifting into it is exactly the
   * mistake this flag exists to prevent.
   */
  trap: 'left' | 'right' | null;
}

/** Which way a maneuver sends you across the carriageway. */
function maneuverSide(
  modifier: string | undefined,
  drivingSide: 'left' | 'right',
): 'left' | 'right' | 'straight' | null {
  const m = (modifier ?? '').toLowerCase();
  if (m === 'uturn') {
    // In left-hand traffic a U-turn swings out to the right, and vice versa.
    return drivingSide === 'left' ? 'right' : 'left';
  }
  if (m.includes('left')) return 'left';
  if (m.includes('right')) return 'right';
  if (m === 'straight' || m === '') return 'straight';
  return null;
}

/**
 * Turn a lane array into positional advice.
 *
 * `drivingSide` is where traffic keeps by default ('left' in Thailand).
 * `maneuverModifier`, when supplied, is used as a SAFETY CROSS-CHECK: lane data
 * that says "use the left lanes" for a right turn cannot be describing this
 * junction, and acting on it is how a driver ends up in an exit-only lane
 * heading somewhere else entirely. When the two disagree we return null and
 * show no lane guidance at all - saying nothing is safe, saying the wrong
 * thing is not.
 */
export function laneAdvice(
  lanes: Lane[] | null | undefined,
  drivingSide: 'left' | 'right' = 'left',
  maneuverModifier?: string,
): LaneAdvice | null {
  if (!lanes?.length) return null;

  const total = lanes.length;
  const validIndices = lanes.map((l, i) => (laneValid(l) ? i : -1)).filter((i) => i >= 0);
  const validCount = validIndices.length;

  // No lane data worth advising on: everything valid, or nothing is (bad data).
  if (validCount === 0) {
    return { lanes, total, validIndices, side: 'all', validCount, changeRequired: false, trap: null };
  }

  const first = validIndices[0];
  const last = validIndices[validIndices.length - 1];
  const changeRequired = validCount < total;

  let side: LaneAdvice['side'] = 'all';
  if (!changeRequired) side = 'all';
  else if (first === 0) side = 'left';
  else if (last === total - 1) side = 'right';
  else side = 'centre';

  // Safety cross-check. Mapbox occasionally hands back the lane model for a
  // neighbouring junction; positional advice derived from it points the driver
  // the wrong way across the carriageway. Refuse to guess.
  const wanted = maneuverSide(maneuverModifier, drivingSide);
  if (
    changeRequired &&
    ((wanted === 'left' && side === 'right') || (wanted === 'right' && side === 'left'))
  ) {
    return null;
  }

  // Second check, using what the lanes themselves claim rather than where they
  // sit. Mapbox marks the indication that completes the maneuver, so on a left
  // turn at least one usable lane should be signed left-ish. Observed in the
  // field: a left turn 40 m ahead whose only "valid" lane was signed STRAIGHT -
  // the lane model belonged to the junction after it. Telling a driver to hold
  // a straight-only lane through a left turn is worse than showing nothing.
  const turning = wanted === 'left' || wanted === 'right';
  if (changeRequired && turning) {
    const signs = validIndices
      .map((i) => lanes[i].valid_indication)
      .filter((s): s is LaneIndication => !!s);
    // Only judge when Mapbox actually told us; otherwise fall back to position.
    if (signs.length && !signs.some((s) => s.includes(wanted) || s === 'uturn')) return null;
  }

  // The trap: the lane the driver is most likely already in cannot be used.
  const defaultLane = drivingSide === 'left' ? 0 : total - 1;
  const trap =
    changeRequired && !laneValid(lanes[defaultLane])
      ? drivingSide === 'left'
        ? 'left'
        : 'right'
      : null;

  return { lanes, total, validIndices, side, validCount, changeRequired, trap };
}

/**
 * How far out lane guidance should appear, in metres.
 * Scales with speed: at 100 km/h you need ~25 s to cross three lanes safely,
 * at 30 km/h in town 250 m is ample and anything more is clutter.
 */
export function laneVisibilityRange(speedMs: number): number {
  return Math.min(1400, Math.max(250, speedMs * 25));
}
